const DAY_ORDER = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const TIMEZONE_ALIASES = {
  "asia/calcutta": "Asia/Kolkata",
};

const getNormalizedTimezone = (timezone) => {
  if (!timezone || typeof timezone !== "string") {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  const normalizedKey = timezone.trim().toLowerCase();
  return TIMEZONE_ALIASES[normalizedKey] || timezone;
};

const getMinutesFromTimeString = (timeStr, { treatMidnightAsEndOfDay = false } = {}) => {
  if (!timeStr || typeof timeStr !== "string") {
    return null;
  }

  const parts = timeStr.trim().split(/\s+/);
  const timePart = parts[0];
  const period = parts[1]?.toUpperCase();

  if (!timePart || !period) {
    return null;
  }

  const [hoursText, minutesText = "0"] = timePart.split(":");
  const hours = Number.parseInt(hoursText, 10);
  const minutes = Number.parseInt(minutesText, 10);

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 1 ||
    hours > 12 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  if (period === "AM" && hours === 12) {
    return treatMidnightAsEndOfDay ? 24 * 60 : 0;
  }

  const hour24 = period === "PM" && hours !== 12 ? hours + 12 : hours;
  return hour24 * 60 + minutes;
};

const getZonedNowParts = (referenceDate, timezone) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: getNormalizedTimezone(timezone),
    weekday: "long",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(referenceDate);
  const weekday = parts.find((part) => part.type === "weekday")?.value?.toLowerCase();
  const hour = Number.parseInt(
    parts.find((part) => part.type === "hour")?.value ?? "0",
    10
  );
  const minute = Number.parseInt(
    parts.find((part) => part.type === "minute")?.value ?? "0",
    10
  );

  return {
    weekday,
    minutes: hour * 60 + minute,
  };
};

export const getMenuTimezone = (menu, locationId) => {
  const matchingLocation = menu?.locationIds?.find(
    (location) => String(location?._id) === String(locationId)
  );

  return getNormalizedTimezone(matchingLocation?.schedule?.timezone);
};

const isScheduleActiveAtMinutes = (schedule, currentMinutes) => {
  if (!schedule?.isEnabled) {
    return false;
  }

  const openMinutes = getMinutesFromTimeString(schedule.openTime);
  const closeMinutes = getMinutesFromTimeString(schedule.closeTime, {
    treatMidnightAsEndOfDay: true,
  });

  if (openMinutes === null || closeMinutes === null) {
    return false;
  }

  if (closeMinutes < openMinutes) {
    return currentMinutes >= openMinutes || currentMinutes < closeMinutes;
  }

  return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
};

const isPreviousDayOvernightActive = (schedule, currentMinutes) => {
  if (!schedule?.isEnabled) {
    return false;
  }

  const openMinutes = getMinutesFromTimeString(schedule.openTime);
  const closeMinutes = getMinutesFromTimeString(schedule.closeTime, {
    treatMidnightAsEndOfDay: true,
  });

  if (openMinutes === null || closeMinutes === null) {
    return false;
  }

  if (closeMinutes >= openMinutes) {
    return false;
  }

  return currentMinutes < closeMinutes;
};

export const isMenuOpenNow = (
  menu,
  locationId,
  referenceDate = new Date()
) => {
  if (!menu?.menuSchedule || menu.menuSchedule.length === 0) {
    return true;
  }

  const timezone = getMenuTimezone(menu, locationId);
  const { weekday, minutes } = getZonedNowParts(referenceDate, timezone);

  if (!weekday) {
    return false;
  }

  const todaySchedule = menu.menuSchedule.find(
    (schedule) => schedule?.day?.toLowerCase() === weekday
  );

  if (isScheduleActiveAtMinutes(todaySchedule, minutes)) {
    return true;
  }

  const currentDayIndex = DAY_ORDER.indexOf(weekday);
  if (currentDayIndex === -1) {
    return false;
  }

  const previousDay = DAY_ORDER[(currentDayIndex + DAY_ORDER.length - 1) % DAY_ORDER.length];
  const previousDaySchedule = menu.menuSchedule.find(
    (schedule) => schedule?.day?.toLowerCase() === previousDay
  );

  return isPreviousDayOvernightActive(previousDaySchedule, minutes);
};

export const getCurrentlyOpenMenus = (
  menus,
  locationId,
  referenceDate = new Date()
) => {
  if (!Array.isArray(menus) || menus.length === 0) {
    return [];
  }

  return menus.filter((menu) => isMenuOpenNow(menu, locationId, referenceDate));
};
